import React, {useContext, useEffect, useState} from "react";
import axios from "axios";
import PopperMenu from "../../../../../../../common/components/shared/popperMenu/popperMenu";
import {
    DamageLocation,
    DamageModelWithDirection,
    DamageType,
    DamageTypeFirstLetter,
    EquipmentType
} from "../../../../../../models/tcr";
import {availableDamagesForEquipments} from "../../../../../../models/damages";
import MenuItem from "@mui/material/MenuItem";
import i18n from "../../../../../../../i18n/i18n";
import Checkbox from "../../../../../../../common/components/shared/checkbox/checkbox";
import {ValidatedValue} from "../../../../../../../helpers/validatedValue";
import Button from "../../../../../../../common/components/shared/button/button";
import {storesContext} from "../../../../../../../common/stores/stores";
import "./trailerWithDamages.scss";
import {DateUtils} from "../../../../../../../helpers/dateUtils";
import {Direction, SideType} from "../../../../../../models/transport";
import TcrUtils from "../../../../../../../helpers/tcrUtils";
import Loader from "../../../../../../../common/components/shared/loader/loader";

interface TrailerWithDamagesProps {
    sideType: SideType;
}

export default function TrailerWithDamages(props: TrailerWithDamagesProps): React.ReactElement {
    const [svgElement] = useState(document.getElementsByClassName("trailer-svg"));
    const [menuIsOpen, setMenuIsOpen] = useState<boolean>(false);
    const [anchorMenu, setAnchorMenu] = useState<HTMLElement | null>(null);
    const [equipmentType, setEquipmentType] = useState<EquipmentType | null>(null);
    const [damageLocation, setDamageLocation] = useState<DamageLocation | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const tcrStore = useContext(storesContext).tcrStore;
    const direction = tcrStore.direction;

    useEffect(() => {
        switch (props.sideType) {
            case SideType.FRONT:
                setSvgMap("/assets/trailer/front.svg");
                break;
            case SideType.RIGHT:
                setSvgMap("/assets/trailer/right.svg");
                break;
            case SideType.REAR:
                setSvgMap("/assets/trailer/rear.svg");
                break;
            case SideType.LEFT:
                setSvgMap("/assets/trailer/left.svg");
                break;
        }
    }, []);

    window.svgClick = (event: React.MouseEvent, equipmentType?: EquipmentType, damageLocation?: DamageLocation) => {
        svgClick(event as React.MouseEvent<HTMLElement>, equipmentType, damageLocation);
    };

    return (
        <div className="trailer-with-damages">
            {isLoading && <Loader />}
            <div className="trailer-svg"/>
            {renderDamageList()}
        </div>
    );

    function renderDamageList(): React.ReactElement {
        return (
            <>
                {anchorMenu && (
                    <PopperMenu
                        isOpen={menuIsOpen}
                        setOpen={(isOpen) => handleSetOpen(isOpen)}
                        anchorEl={anchorMenu}
                        testId="damages-list"
                        doNotCloseAt="more"
                        className="trailer-with-damages-menu"
                        placement="auto"
                    >
                        <div>
                            {renderHeaderInDamageList()}
                            {renderDamageListContent()}
                            <div className="buttons">
                                <Button
                                    type="secondary"
                                    onClick={closeDamagesMenu}
                                    testId="cancelBtn"
                                    text={i18n.t("cancel")}/>
                                <Button
                                    type="primary"
                                    onClick={addDamages}
                                    testId="ok"
                                    text={i18n.t("transportation.order.tcr.sections.trailerCondition.modifyTrailerDamages.saveButtonText")}
                                />
                            </div>
                        </div>
                    </PopperMenu>
                )}
            </>
        );
    }

    function renderHeaderInDamageList(): React.ReactElement {
        return (
            <div className="header">
                <span>
                    {i18n.t("transportation.order.tcr.sections.trailerCondition.modifyTrailerDamages.header")}
                </span>
                <div>
                    <span>
                        - {i18n.t(`transportation.order.tcr.equipmentType.${equipmentType}`)}
                    </span>
                    {damageLocation && (
                        <span> - {i18n.t(`transportation.order.tcr.sections.trailerCondition.damageLocation.${damageLocation}`)}</span>
                    )}
                </div>
            </div>
        );
    }

    function renderDamageListContent(): React.ReactElement[] | undefined {
        const damageList = equipmentType ? availableDamagesForEquipments.get(equipmentType) : undefined;

        if (!damageList || !equipmentType || !direction) {
            return undefined;
        }

        return (
            damageList.map((damageType: DamageType) => {
                const damage: DamageModelWithDirection = {
                    damageType,
                    direction,
                    equipmentType,
                    sideType: props.sideType,
                    damageLocation,
                    damageUpdateTime: DateUtils.getFormattedDateWithTimeInUTC(new Date())
                };

                const validatedValue = new ValidatedValue(TcrUtils.isEquipmentDamaged(tcrStore.damages, damage));

                return (
                    <MenuItem className="menu-item-wrapper" key={damageType}>
                        <Checkbox
                            validatedValue={validatedValue}
                            testId={`checkbox-${damageType}`}
                            label={i18n.t(`transportation.order.tcr.sections.trailerCondition.damageType.${damageType}`)}
                            onChange={(value: boolean) => {
                                if (value) {
                                    tcrStore.addSelectedDamage(damage);
                                } else {
                                    tcrStore.removeSelectedDamage(damage);
                                }
                            }}
                        />
                    </MenuItem>
                );
            })
        );
    }

    function setSvgMap(svgUrl: string): void {
        setIsLoading(true);
        downloadSvgFromBlob(svgUrl).then((svg: Blob) => {
            if (svg && svg.text) {
                svg.text().then(async (resp: string) => {
                    svgElement[0].innerHTML = resp;

                    if (svgElement[0].querySelector("svg")) {
                        Array.from(svgElement[0]!.querySelector("svg")!.children).forEach((element) => {
                            element.classList.add("more");

                            if (element.id.includes(".damage")) {
                                element.classList.add("damage-circle");
                                element.classList.add(element.id);
                            }

                            if (typeof (element as HTMLElement).onclick === "function") {
                                element.classList.add("clickable-trailer-equipment");
                            }
                        });
                    }

                    markDamages();
                    setIsLoading(false);
                });
            }
        });
    }

    async function downloadSvgFromBlob(url: string): Promise<Blob> {
        const response = await axios.get<Blob>(url, {responseType: "blob" as "json", headers: {}});
        const dataType = response?.data?.type;
        const binaryData = [];
        binaryData.push(response?.data);
        return new Blob(binaryData, {type: dataType});
    }

    function svgClick(event: React.MouseEvent<HTMLElement>, equipmentType?: EquipmentType, damageLocation?: DamageLocation): void {
        if (!equipmentType) {
            return;
        }

        setEquipmentType(equipmentType);
        setDamageLocation(damageLocation);
        setAnchorMenu(event.currentTarget);
        setMenuIsOpen(true);
    }

    function addDamages(): void {
        tcrStore.copySelectedDamagesToDamages();
        tcrStore.setIsEdit(true);
        setMenuIsOpen(false);

        setTimeout(() => {
            unmarkDamages();
            markDamages();
        }, 10);
    }

    function closeDamagesMenu(): void {
        tcrStore.copyDamagesToSelectedDamages();
        setMenuIsOpen(false);
    }

    function handleSetOpen(isOpen: boolean): void {
        if (isOpen) {
            tcrStore.copyDamagesToSelectedDamages();
        } else {
            tcrStore.copyDamagesToSelectedDamages();
        }

        setMenuIsOpen(isOpen);
    }

    function unmarkDamages(): void {
        Array.from(svgElement[0]!.querySelector("svg")!.children).forEach((element) => {
            element.classList.remove("marked-damage-circle");

            const span = element.querySelector("text tspan");
            if (span) {
                span!.textContent = "";
            }

            Array.from(element.children).forEach((subElement) => {
                subElement.classList.remove("damage-in");
                subElement.classList.remove("damage-out");
            });
        });
    }

    function markDamages(): void {
        const damages = tcrStore.damages;

        damages.map((damage: DamageModelWithDirection) => {
            const damageType = damage.damageType;
            const equipmentType = damage.equipmentType;
            const damageLocation = damage.damageLocation;
            const damageDirection = damage.direction;
            const sideType = damage.sideType;

            if (sideType !== props.sideType) {
                return;
            }

            Array.from(svgElement[0]!.querySelector("svg")!.children).forEach((element) => {
                const damageEquipmentWithLocation = !damageLocation ? `${equipmentType}` : `${equipmentType}__${damageLocation}`;
                const isDamageMarked = element.id.includes(`${damageEquipmentWithLocation}.damage`);

                if (isDamageMarked) {
                    element.classList.add("marked-damage-circle");

                    const allDamagesInTheSameLocation = TcrUtils.allDamagesInTheSameLocation(damages, damage);
                    if (allDamagesInTheSameLocation.length > 1) {
                        markMultipleDamages(element, damageDirection, damageEquipmentWithLocation, allDamagesInTheSameLocation);
                    } else {
                        markSingleDamage(element, damageType, damageDirection, damageEquipmentWithLocation);
                    }
                }
            });

        });
    }

    function markSingleDamage(element: Element, damageType: DamageType, damageDirection: Direction, damageEquipmentWithLocation: string): void {
        const span = element.querySelector("text tspan");
        if (span) {
            let damageTypeFirstLetter = DamageTypeFirstLetter[damageType] as string;

            if (damageTypeFirstLetter.length === 1) {
                damageTypeFirstLetter = " " + damageTypeFirstLetter;  // center damage type first letter
            }

            span!.textContent = damageTypeFirstLetter;
        }

        Array.from(element.children).forEach((subElement) => {
            const subElementClassList = subElement.classList;

            drawInOrOutMark(subElement, damageDirection, damageEquipmentWithLocation);

            if (direction === Direction.IN && damageDirection === Direction.IN && subElementClassList.contains("damage-in") && subElementClassList.contains("damage-out")) {
                subElementClassList.remove("damage-out");
            }
        });
    }

    function markMultipleDamages(element: Element, damageDirection: Direction, damageEquipmentWithLocation: string, allDamagesInTheSameLocation: DamageModelWithDirection[]): void {
        Array.from(element.children).forEach((subElement) => {
            const numberOfDirectionIn = allDamagesInTheSameLocation.filter(damage => damage.direction === Direction.IN).length;
            const numberOfDirectionOut = allDamagesInTheSameLocation.filter(damage => damage.direction === Direction.OUT).length;

            if (numberOfDirectionIn && numberOfDirectionOut) {
                drawInAndOutMark(subElement, damageEquipmentWithLocation);
            } else {
                drawInOrOutMark(subElement, damageDirection, damageEquipmentWithLocation);
            }
        });
    }

    function drawInOrOutMark(subElement: Element, damageDirection: Direction, damageEquipmentWithLocation: string): void {
        const _isBigCircle = isBigCircle(subElement, damageEquipmentWithLocation);
        const _isSmallCircle = isSmallCircle(subElement, damageEquipmentWithLocation);
        const subElementClassList = subElement.classList;

        if (damageDirection === Direction.IN) {
            if (_isBigCircle || _isSmallCircle) {
                subElementClassList.add("damage-in");
            }
        }
        if (damageDirection === Direction.OUT) {
            if (_isBigCircle || _isSmallCircle) {
                subElementClassList.add("damage-out");
            }
        }
    }

    function drawInAndOutMark(subElement: Element, damageEquipmentWithLocation: string): void {
        const _isBigCircle = isBigCircle(subElement, damageEquipmentWithLocation);
        const _isSmallCircle = isSmallCircle(subElement, damageEquipmentWithLocation);
        const subElementClassList = subElement.classList;

        if (_isBigCircle) {
            subElementClassList.add("damage-in");
        }
        if (_isSmallCircle) {
            subElementClassList.add("damage-out");
        }
    }

    function isBigCircle(subElement: Element, damageEquipmentWithLocation: string): boolean {
        return subElement.id.includes(`${damageEquipmentWithLocation}.damage.big`);
    }

    function isSmallCircle(subElement: Element, damageEquipmentWithLocation: string): boolean {
        return subElement.id.includes(`${damageEquipmentWithLocation}.damage.small`);
    }
}
